import { defineStore } from 'pinia';
import { authApi } from '@/services/api/endpoints';
import { registerAuthBridge, ApiError } from '@/services/api/client';
import { tokenStore } from '@/services/auth/tokenStore';
import { deviceLock } from '@/services/auth/deviceLock';
import { localDb } from '@/services/db/localDb';
import { kv } from '@/services/platform';
import type { User } from '@/types/domain';

const USER_KEY = 'td.auth.user';

type Status = 'anonymous' | 'authenticated';

interface State {
  user: User | null;
  status: Status;
  locked: boolean;
  loginError: string | null;
  loggingIn: boolean;
  ready: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    status: 'anonymous',
    locked: false,
    loginError: null,
    loggingIn: false,
    ready: false,
  }),
  getters: {
    isAuthenticated: (s) => s.status === 'authenticated',
    isChild: (s) => s.user?.role === 'child',
    displayName: (s) => s.user?.displayName ?? '',
  },
  actions: {
    /** Регистрирует мост для api-клиента (refresh/доступ/разлогин). Вызвать один раз. */
    installBridge() {
      registerAuthBridge({
        getAccessToken: () => tokenStore.getAccess(),
        refresh: () => this.refresh(),
        onAuthFailure: () => {
          void this.hardLogout();
        },
      });
    },

    /** Восстановление сессии при старте: есть refresh → authenticated + блокировка (cold start). */
    async restore() {
      const [refresh, cachedUser] = await Promise.all([
        tokenStore.getRefresh(),
        kv.getJson<User>(USER_KEY),
      ]);
      if (refresh && cachedUser) {
        this.user = cachedUser;
        this.status = 'authenticated';
        this.locked = true; // device-lock при каждом cold start (§4.3)
      } else {
        this.status = 'anonymous';
        this.locked = false;
      }
      this.ready = true;
    },

    async login(phone: string, password: string) {
      this.loggingIn = true;
      this.loginError = null;
      try {
        const res = await authApi.login({ phone, password });
        tokenStore.setAccess(res.accessToken);
        await tokenStore.setRefresh(res.refreshToken);
        await kv.setJson(USER_KEY, res.user);
        this.user = res.user;
        this.status = 'authenticated';
        this.locked = false; // только что вошли — не блокируем сразу
        return true;
      } catch (e) {
        this.loginError =
          e instanceof ApiError ? e.message : 'Не удалось войти. Проверьте соединение.';
        return false;
      } finally {
        this.loggingIn = false;
      }
    },

    /** Тихий refresh: возвращает true при успехе (для api-bridge). */
    async refresh(): Promise<boolean> {
      const refresh = await tokenStore.getRefresh();
      if (!refresh) return false;
      try {
        const res = await authApi.refresh(refresh);
        tokenStore.setAccess(res.accessToken);
        await tokenStore.setRefresh(res.refreshToken);
        await kv.setJson(USER_KEY, res.user);
        this.user = res.user;
        this.status = 'authenticated';
        return true;
      } catch {
        return false;
      }
    },

    /** Разблокировка экрана /lock. На web — по жесту, на native — биометрия. */
    async unlock(): Promise<boolean> {
      const ok = await deviceLock.authenticate();
      if (!ok) return false;
      this.locked = false;
      // освежаем access после разблокировки (offline — остаёмся на кэше)
      void this.refresh();
      return true;
    },

    /** Перезаблокировать (resume/таб неактивен на native). */
    lock() {
      if (this.status === 'authenticated') this.locked = true;
    },

    /** Явный выход: отзыв refresh на сервере + локальная очистка. */
    async logout() {
      const refresh = await tokenStore.getRefresh();
      if (refresh) {
        try {
          await authApi.logout(refresh);
        } catch {
          /* offline — всё равно чистим локально */
        }
      }
      await this.hardLogout();
    },

    /** Локальная очистка сессии (без обращения к серверу). */
    async hardLogout() {
      await tokenStore.clear();
      await kv.remove(USER_KEY);
      await localDb.clearAll(); // не оставлять кэш/outbox прошлого пользователя (MAJOR-6)
      this.user = null;
      this.status = 'anonymous';
      this.locked = false;
    },
  },
});
